/** @jsx jsx */

import React from "react";

import { jsx, SerializedStyles } from "@emotion/core";

import { Typography, Checkbox } from "antd";

import { Field, FieldProps, ErrorMessage } from "formik";

import { formHelperLabels as LABELS } from "./FormHelperLabels";
import { formHelperStyles } from "./FormHelperStyles";
import { CheckboxChangeEvent } from "antd/lib/checkbox";

const { Text } = Typography;

interface Props {
  name: string;
  /**
   *  by default "name" is used to indicate the field name in UI
   *  but if a custom label is to be shown this overrides "name"
   */
  label?: string | React.ReactNode;

  mandatory?: boolean;

  disabled?: boolean;

  hideError?: boolean;

  className?: string;

  customError?: string;
  /**
   * props.placeholder is given highest priority
   * fallback: label or name
   */
  placeholder?: string;

  /**
   * customStyles provided overrides default styles using
   * "Composition" behavior of emotion.
   * reference: https://emotion.sh/docs/composition
   */
  customStyles?: SerializedStyles;
  onChange?: (e: CheckboxChangeEvent) => void;
  onBlur?: (e: React.FocusEvent<HTMLInputElement>) => void;
}

/**
 *
 * @param {Props} props
 * @returns a reusable, customizable checkbox component to be used only within context of formik.
 */
const FormCheckboxComponent: React.FC<Props> = (props: Props) => {
  const {
    name,
    label,
    mandatory,
    disabled,
    hideError,
    customError,
    customStyles,
    onChange,
    onBlur,
    className
  } = props;
  const renderCheckBoxForm = (
    <div className={className} css={[formHelperStyles, customStyles]}>
      <Field name={name}>
        {({ field }: FieldProps) => (
          <Checkbox
            {...field.checked}
            name={field.name}
            onChange={onChange || field.onChange}
            disabled={disabled}
            onBlur={onBlur || field.onBlur}
          >
            {label}
            <Text className="mandatory">{mandatory && LABELS.mandatory}</Text>
          </Checkbox>
        )}
      </Field>
      {!hideError && (
        <ErrorMessage name={name}>
          {msg => {
            const errorMessage = customError || msg; //To make sure only 1 error message is shown.
            return <Typography className="error">{errorMessage}</Typography>;
          }}
        </ErrorMessage>
      )}
    </div>
  );
  return renderCheckBoxForm;
};

export default FormCheckboxComponent;
